<template>
  <section class="pyramid-diagram statistics">
    <Breadcrumbs
      :breadcrumbs="[
        { title: $t('menu.statistics'), disabled: true },
        { title: $t('menu.pyramiddiagram'), disabled: true }
      ]">
    </Breadcrumbs>

    <form class="evollu-form">

      <div class="row" v-if="clusters.length">
        <div class="col-12 col-sm-5">
          <va-switch v-model="filters.showEntityStats">
            <span slot="trueTitle">{{$t('kiosks.entity')}}</span>
            <span slot="falseTitle">
              <span>
                {{$t('statistics.cluster')}}
              </span>
              <span v-if="!filters.showEntityStats && filters.cluster" @click.prevent="expandCluster = !expandCluster" class="cluster-dropdown">
                <i class="fa" :class="{'fa-chevron-up': expandCluster, 'fa-chevron-down': !expandCluster}"></i>
              </span>
            </span>
          </va-switch>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-sm-5">
          <span v-if="filters.showEntityStats">
            <label class="form-label">{{$t('statistics.select_entity')}}:</label>
            <multiselect
              id="entities-multiselect"
              v-model="filters.entity"
              :options="entityOptions"
              track-by="id"
              :custom-label="formatedEntityLabel"
              :placeholder="!entityOptions.length ? $t('statistics.no_entities') : $t('statistics.totalValues')"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="entityOptions.length < 2"
            >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              <template slot="clear" slot-scope="props" v-if="entityOptions.length > 1 && filters.entity !== null">
                <div id="entities-multiselect-clear" class="multiselect__clear" @mousedown.prevent.stop="filters.entity = null"></div>
              </template>
            </multiselect>
          </span>

          <span v-else>
            <label class="form-label">{{$t('statistics.select_cluster')}}:</label>
            <multiselect
              id="clusters-multiselect"
              v-model="filters.cluster"
              :options="clusterOptions"
              track-by="id"
              :custom-label="formatedEntityLabel"
              :placeholder="!clusterOptions.length ? $t('statistics.no_clusters') : $t('statistics.totalValues')"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="clusterOptions.length < 2"
            >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              <template slot="clear" slot-scope="props" v-if="clusterOptions.length > 1 && filters.cluster !== null">
                <div id="clusters-multiselect-clear" class="multiselect__clear" @mousedown.prevent.stop="filters.cluster = null"></div>
              </template>
            </multiselect>
          </span>
        </div>

        <div class="col-12 col-sm-3">
          <label class="form-label">{{$t('statistics.date_field')}}:</label>
          <multiselect
            v-model="filters.dateType"
            :options="dateOptions"
            select-label=""
            deselect-label="×"
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            :searchable="true"
            :disabled="isLoading"
          >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
            <template slot="singleLabel" slot-scope="props">
              <span class="option__name">{{ $t(`statistics.${props.option}` )}}</span>
            </template>
            <template slot="option" slot-scope="props">
              <span class="option__name">{{ $t(`statistics.${props.option}`) }}</span>
            </template>
          </multiselect>
        </div>

        <div class="col-12 col-sm-4">
          <RangeDatePicker
            id="range-date-picker"
            :label="`${$t('statistics.date_interval')}:`"
            v-model="filters.dateRange"
            placeholder="---"
            :clearable="false"
            :disabled="isLoading"
          >
          </RangeDatePicker>
        </div>
      </div>

      <div class="row">
        <!-- Audiology Providers Filter -->
        <div class="col-12 col-md-4">
          <label class="form-label">{{$t('agenda.screenings.labels.audiology_providers')}}:</label>
          <multiselect
            :options="availableAudProviders"
            v-model="filters.audProviders"
            track-by="id"
            label="name"
            select-label=""
            selected-label=""
            deselect-label="×"
            :custom-label="formatedEntityLabel"
            :placeholder="$t('agenda.screenings.labels.all_entities')"
            :allow-empty="true"
            :multiple="true"
            :searchable="true"
            :limit="10"
            :limit-text="(count) => $t('tables.multiselect_limitText_and') + count + this.$t('tables.multiselect_limitText_others')">
            <template slot="clear" slot-scope="props">
              <div class="multiselect__clear" v-if="filters.audProviders && filters.audProviders.length" @mousedown.prevent.stop="filters.audProviders = []"></div>
            </template>
            <span slot="noResult">{{'patients.noResults'| translate}}</span>
          </multiselect>
        </div>
      </div>

      <div v-if="!filters.showEntityStats && filters.cluster && expandCluster" class="row mb-5">
        <div class="col-12">
          <va-badge outline v-for="(name, index) in filters.cluster.entity_names" :key="index">
            {{name}}
          </va-badge>
        </div>
      </div>
    </form>

    <div v-if="isLoading" class="text-center no-data-wrapper">
      <h6><b>{{$t('tables.loading')}}</b></h6>
      <LoadingAnimation :size="'5px'" class="d-flex justify-content-center pt-4 pb-4"></LoadingAnimation>
    </div>

    <div v-show="!isLoading && !hasSubmittedData" class="no-data-wrapper">
      <span v-show="!hasData" class="is-bold">{{$t('pyramid.no_data')}}</span>
      <span v-show="hasData" class="is-bold">{{$t('pyramid.no_submitted_data')}}</span>
    </div>

    <div v-show="!isLoading && (hasData || hasSubmittedData)" class="section">
      <h5 class="section-title">{{$t('statistics.registos')}}</h5>

      <div v-show="hasData" class="d-flex justify-content-between align-items-center">
        <div class="col-12 col-sm-2 chart-label">
          <span v-if="filters.showEntityStats">
            <span>
              {{$t('pyramid.selectedEntities')}}: {{ filters.entity === null ? entityOptions.length : 1 }}
            </span>
            <br>
            <span v-if="filters.entity === null">
              {{$t('pyramid.entitiesWithLeads')}}: {{ entitiesWithLeads }}<br>
            </span>
          </span>
          <span v-else>
            <span v-if="filters.cluster">
            {{$t('pyramid.selectedEntities')}}: {{ filters.cluster.entitiesWithPermissions }}<br>
            </span>
            <span>
              {{$t('pyramid.entitiesWithLeads')}}: {{ entitiesWithLeads }}<br>
            </span>
          </span>
          <span>
            {{$t('pyramid.sessions')}}: {{ sessions }}<br>
          </span>
          <span>
            {{$t('pyramid.sessionEntries')}}: {{ sessionEntries }}
          </span>
        </div>
        <div class="col-12 col-sm-2 chart-label text-center">
          {{$t('pyramid.hearingTests')}}: {{ cancelledHtLoss + cancelledHtNoLoss + ht }}
        </div>
        <div class="col-0 offset-sm-1 col-sm-2"></div>
      </div>

      <div v-show="hasSubmittedData" id="chartdiv" ref="chartdiv"></div>

      <div v-show="hasSubmittedData" class="d-flex justify-content-end mr-3">
        <div class="pull-right text-end chart-label">
          {{$t('statistics.status.101')}}: {{ appointmentNotApplicableCount }} ({{((appointmentNotApplicableCount / totalEntries) * 100).toFixed(2)}}%)<br>
          {{$t('statistics.status.106')}}: {{ appointmentEntCount }} ({{((appointmentEntCount / totalEntries) * 100).toFixed(2)}}%)<br>
          {{$t('statistics.status.102')}}: {{ appointmentNoSaleCount }} ({{((appointmentNoSaleCount / totalEntries) * 100).toFixed(2)}}%)<br>
          {{$t('statistics.status.103')}}: {{ appointmentPossibleSaleCount }} ({{((appointmentPossibleSaleCount / totalEntries) * 100).toFixed(2)}}%)<br>
          {{$t('statistics.status.104')}}: {{ trialCount }} ({{((trialCount / totalEntries) * 100).toFixed(2)}}%)<br>
          {{$t('statistics.status.110')}}: {{ buysCount }} ({{((buysCount / totalEntries) * 100).toFixed(2)}}%)
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import Multiselect from 'vue-multiselect'
import * as am4core from '@amcharts/amcharts4/core'
import { StatisticsService } from '@/api/LeadsService'
import * as am4charts from '@amcharts/amcharts4/charts'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import { PYRAMID_STATS } from '@/local-storage/LocalStorageKeys'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import LoadingAnimation from '@/components/users/components/Helpers/LoadingAnimation'
import am4themes_animated from '@amcharts/amcharts4/themes/animated' // eslint-disable-line
// import am4themes_spiritedaway from '@amcharts/amcharts4/themes/spiritedaway' // eslint-disable-line

am4core.useTheme(am4themes_animated)
// am4core.useTheme(am4themes_spiritedaway)

export default {
  components: {
    Breadcrumbs,
    Multiselect,
    RangeDatePicker,
    LoadingAnimation
  },
  mixins: [
    MultiselectLabelFormatter
  ],
  data () {
    return {
      expandCluster: false,
      chart: null,
      isLoading: false,
      dataset: {},
      totalValues: null,
      filters: {
        showEntityStats: true,
        entity: null,
        cluster: null,
        audProviders: [],
        dateRange: {
          start: this.moment().subtract(1, 'months').format('YYYY-MM-DD'),
          end: this.moment().format('YYYY-MM-DD')
        },
        dateType: 'created_at'
      },
      dateOptions: [
        'created_at',
        'updated_at',
        'qualified_at'
      ]
    }
  },
  methods: {
    createChart: function () {
      let chart = am4core.create('chartdiv', am4charts.SlicedChart)

      chart.hiddenState.properties.opacity = 0

      chart.data = this.chartData

      var series = chart.series.push(new am4charts.PyramidSeries())

      series.colors.list = [
        am4core.color('#24433E'),
        am4core.color('#266F62'),
        am4core.color('#279381'),
        am4core.color('#28B199'),
        am4core.color('#29C4A9'),
        am4core.color('#68D5C2'),
        am4core.color('#97E2D5')
      ]
      // series.colors.step = 2
      series.dataFields.value = 'valor'
      series.dataFields.category = 'estado'
      // series.labels.template.disabled = true
      // series.valueIs = 'height'
      series.topWidth = am4core.percent(100)
      series.bottomWidth = am4core.percent(0)

      series.alignLabels = true
      // series.labels.template.disabled = true
      // series.ticks.template.disabled = true

      series.labels.template.text = '{category}: {valor.formatNumber("#.")} ({percentagem}%)\n {morelabel}'
      series.slices.template.tooltipText = '{category}: {valor.formatNumber("#.")} ({percentagem}%)'

      chart.legend = new am4charts.Legend()
      chart.legend.position = 'left'
      chart.legend.valign = 'bottom'
      chart.legend.width = 250
      chart.legend.labels.template.maxWidth = 150
      chart.legend.labels.template.truncate = false
      chart.legend.labels.template.wrap = true
      chart.legend.margin(5, 5, 20, 5)
      chart.legend.valueLabels.template.text = '{valor.formatNumber("#.")} ({percentagem}%)'

      // chart.dataFields.fromName = 'from'
      // chart.dataFields.toName = 'to'
      // chart.dataFields.value = 'value'
      // chart.links.template.colorMode = 'gradient'
      // chart.paddingTop = 5
      // chart.paddingRight = 150
      // chart.paddingBottom = 30
      // chart.nodes.template.nameLabel.label.truncate = false
      // chart.nodes.template.nameLabel.label.wrap = true

      chart.exporting.menu = new am4core.ExportMenu()
      chart.exporting.filePrefix = 'LeadsEvollu'

      chart.exporting.menu.items = [{
        'label': '...',
        'menu': [
          { 'type': 'png', 'label': 'PNG' },
          { 'type': 'xlsx', 'label': 'XLSX' },
          { 'label': 'Print', 'type': 'print' }
        ]
      }]

      chart.exporting.dataFields = { 'estado': 'Estado', 'leads': 'Leads', 'percentagem': 'Percentagem' }

      this.chart = chart
    },
    loadBaseData () {
      if(!this.isLoading) {
        this.isLoading = true
        StatisticsService.getFullStats(this.requestParams)
          .then(originalData => this.createDataset(originalData))
          .catch((error) => {
            console.log(error)
            this.showToast(this.$t('statistics.something_went_wrong'), this.$store.state.errorToastOptions)
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    createDataset: function (data) {
      this.dataset = {}
      this.totalValues = null

      if (data.length) {
        data
          .sort((a, b) => a.id - b.id)
          .forEach(element => {
            this.dataset[element.id] = this.createDatasetEntry(element)
          })

        this.totalValues = this.calculateAggregateData(Object.keys(this.dataset))
      }
    },
    calculateAggregateData: function (entityIds) {
      let aggregate = this.createDatasetEntry()
      const keys = Object.keys(aggregate)
      aggregate.entities = 0

      entityIds.forEach(entityId => {
        if (this.dataset.hasOwnProperty(entityId)) {
          let element = this.dataset[entityId]

          keys.forEach(key => {
            aggregate[key] += element[key]
          })

          if (element.kiosk_ht > 0 || element.cancelled > 0 || element.kiosk > 0 || element.counter > 0 || element.app > 0 || element.web > 0) {
            aggregate.entities++
          }
        }
      })

      return aggregate
    },
    createDatasetEntry: function (data = {}) {
      return {
        counter: 0,
        kiosk: 0,
        app: 0,
        web: 0,
        kiosk_ht: 0,
        kiosk_ht_loss: 0,
        questionnaires: 0,
        contacts_no_success: 0,
        contacts_no_success_hl: 0,
        contacts_success: 0,
        contacts_success_hl: 0,
        contacts_postponed: 0,
        contacts_postponed_hl: 0,
        requested_appointment: 0,
        requested_appointment_hl: 0,
        converted_to_appointment: 0,
        converted_to_appointment_hl: 0,
        appointment_not_applicable: 0,
        appointment_not_applicable_hl: 0,
        appointment_no_sale: 0,
        appointment_no_sale_hl: 0,
        appointment_possible_sale: 0,
        appointment_possible_sale_hl: 0,
        trial: 0,
        trial_hl: 0,
        qualified: 0,
        submitted_hl: 0,
        appointment_missed: 0,
        appointment_missed_hl: 0,
        appointment_ent: 0,
        appointment_ent_hl: 0,
        sale: 0,
        sale_hl: 0,
        cancelled: 0,
        cancelled_ht_loss: 0,
        cancelled_ht_no_loss: 0,
        sessions: 0,
        leads_sessions: 0,
        entities: 0,
        ...data
      }
    },
    getFullEntityById: function (id) {
      return this.fullEntities.find(e => e.id === id)
    },
    loadAudProviderFilter (providers) {
      this.filters.audProviders = this.availableAudProviders.filter(provider => providers.some(providerId => providerId === provider.id))
    },
    loadLocalStorage: function () {
      return new Promise((resolve, reject) => {
        const state = LocalStorageHelper.get(PYRAMID_STATS)
        if (state !== null) {
          this.filters = {
            ...this.filters,
            ...state
          }
          if (state.audProviders !== '') {
            this.loadAudProviderFilter(state.audProviders.split(',').map(provider => parseInt(provider)))
          }
        }
        resolve()
      })
    }
  },
  computed: {
    ...mapGetters([
      'clusters',
      'availableAudProviders',
    ]),
    audiologyService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.evollead_server)
    },
    fullEntities: function () {
      return this.audiologyService ? this.audiologyService.entities : []
    },
    requestParams: function () {
      return {
        from: this.filters.dateRange.start,
        until: this.filters.dateRange.end,
        date: this.filters.dateType
      }
    },
    stats: function () {
      if (this.filters.showEntityStats && this.filters.entity) {
        return this.dataset[this.filters.entity.id]
      } else if (!this.filters.showEntityStats && this.filters.cluster) {
        return this.calculateAggregateData(this.filters.cluster.entitiesIds)
      } else {
        return this.totalValues
      }
    },
    entityOptions: function () {
      return Object.values(this.dataset)
        .map(element => {
          let entity = this.getFullEntityById(element.id)
          return {
            id: element.id,
            name: element.name,
            cities: entity ? entity.cities : []
          }
        })
    },
    clusterOptions: function () {
      return this.clusters
        .map(cluster => {
          let entitiesWithPermissions = this.entityOptions.filter(entity => cluster.entitiesIds.some(entityId => entity.id === entityId))
          cluster.entitiesWithPermissions = entitiesWithPermissions.length
          cluster.entity_names = entitiesWithPermissions.map(e => e.name)
          return cluster
        })
        .filter(cluster => cluster.entitiesWithPermissions > 0)
    },
    entitiesWithLeads: function () {
      return this.stats && this.stats.hasOwnProperty('entities') ? this.stats.entities : 0
    },
    counterEntries: function () {
      return this.stats ? this.stats.counter : 0
    },
    kioskEntries: function () {
      return this.stats ? this.stats.kiosk : 0
    },
    appEntries: function () {
      return this.stats ? this.stats.app : 0
    },
    webEntries: function () {
      return this.stats ? this.stats.web : 0
    },
    totalEntries: function () {
      return this.counterEntries +
            this.kioskEntries +
            this.appEntries +
            this.webEntries
    },
    kioskNoHt: function () {
      return this.stats ? this.stats.kiosk - this.stats.kiosk_ht : 0
    },
    ht: function () {
      return this.stats ? this.stats.kiosk_ht : 0
    },
    htLoss: function () {
      return this.stats ? this.stats.kiosk_ht_loss : 0
    },
    htNoLoss: function () {
      return this.stats ? this.stats.kiosk_ht - this.stats.kiosk_ht_loss : 0
    },
    contactsCount: function () {
      return this.stats ? this.stats.contacts_no_success + this.stats.contacts_success + this.stats.contacts_postponed : 0
    },
    contactsSuccessCount: function () {
      return this.stats ? this.stats.contacts_success : 0
    },
    contactsSuccessHLCount: function () {
      return this.stats ? this.stats.contacts_success_hl : 0
    },
    totalSuccessContacts: function () {
      return this.contactsSuccessCount + this.scheduledAppointments
    },
    contactsNoSuccessCount: function () {
      return this.stats ? this.stats.contacts_no_success : 0
    },
    contactsNoSuccessHLCount: function () {
      return this.stats ? this.stats.contacts_no_success_hl : 0
    },
    contactsPostponedCount: function () {
      return this.stats ? this.stats.contacts_postponed : 0
    },
    contactsPostponedHLCount: function () {
      return this.stats ? this.stats.contacts_postponed_hl : 0
    },
    convertedToAppointmentCount: function () {
      return this.stats ? this.stats.converted_to_appointment : 0
    },
    convertedToAppointmentHLCount: function () {
      return this.stats ? this.stats.converted_to_appointment_hl : 0
    },
    scheduledAppointments: function () {
      return this.convertedToAppointmentCount + this.totalAppointments + this.appointmentMissedCount
    },
    scheduledAppointmentsHL: function () {
      return this.convertedToAppointmentHLCount + this.totalAppointmentsHL + this.appointmentMissedHLCount
    },
    appointmentNotApplicableCount: function () {
      return this.stats ? this.stats.appointment_not_applicable : 0
    },
    appointmentNotApplicableHLCount: function () {
      return this.stats ? this.stats.appointment_not_applicable_hl : 0
    },
    appointmentNoSaleCount: function () {
      return this.stats ? this.stats.appointment_no_sale : 0
    },
    appointmentNoSaleHLCount: function () {
      return this.stats ? this.stats.appointment_no_sale_hl : 0
    },
    appointmentPossibleSaleCount: function () {
      return this.stats ? this.stats.appointment_possible_sale : 0
    },
    appointmentPossibleSaleHLCount: function () {
      return this.stats ? this.stats.appointment_possible_sale_hl : 0
    },
    appointmentMissedCount: function () {
      return this.stats ? this.stats.appointment_missed : 0
    },
    appointmentMissedHLCount: function () {
      return this.stats ? this.stats.appointment_missed_hl : 0
    },
    appointmentEntCount: function () {
      return this.stats ? this.stats.appointment_ent : 0
    },
    appointmentEntHLCount: function () {
      return this.stats ? this.stats.appointment_ent_hl : 0
    },
    totalAppointmentsHL: function () {
      return this.appointmentPossibleSaleHLCount +
            this.appointmentEntHLCount +
            this.appointmentNotApplicableHLCount +
            this.appointmentNoSaleHLCount +
            this.trialHLCount + this.buysHLCount
    },
    totalAppointments: function () {
      return this.appointmentPossibleSaleCount +
            this.appointmentEntCount +
            this.appointmentNotApplicableCount +
            this.appointmentNoSaleCount +
            this.trialCount + this.buysCount
    },
    trialCount: function () {
      return this.stats ? this.stats.trial : 0
    },
    trialHLCount: function () {
      return this.stats ? this.stats.trial_hl : 0
    },
    buysCount: function () {
      return this.stats ? this.stats.sale : 0
    },
    buysHLCount: function () {
      return this.stats ? this.stats.sale_hl : 0
    },
    qualifiedCount: function () {
      return this.stats ? this.stats.qualified : 0
    },
    submittedHLCount: function () {
      return this.stats ? this.stats.submitted_hl : 0
    },
    cancelledCount: function () {
      return this.stats ? this.stats.cancelled : 0
    },
    cancelledHtLoss: function () {
      return this.stats ? this.stats.cancelled_ht_loss : 0
    },
    cancelledHtNoLoss: function () {
      return this.stats ? this.stats.cancelled_ht_no_loss : 0
    },
    sessionEntries: function () {
      return this.stats ? this.stats.leads_sessions : 0
    },
    sessions: function () {
      return this.stats ? this.stats.sessions : 0
    },
    consultationsWithLoss: function () {
      return this.qualifiedCount + this.appointmentEntCount
    },
    daysDifference: function () {
      let from = this.moment(this.fromDate)
      let until = this.moment(this.untilDate)
      return until.diff(from, 'days')
    },
    hasData: function () {
      return this.cancelledCount > 0 || this.totalEntries > 0
    },
    hasSubmittedData: function () {
      return this.totalEntries > 0
    },
    chartData: function () {
      return [
        {
          'estado': this.$t('pyramid.submited'),
          'valor': this.totalEntries,
          'leads': this.totalEntries,
          'percentagem': 100,
          'morelabel': '• ' + this.$t('pyramid.submitedKiosk') + ': ' + this.kioskEntries + ' (' + ((this.kioskEntries / (this.totalEntries)) * 100).toFixed(2) + '%)\n• ' + this.$t('pyramid.counterEntries') + ': ' + this.counterEntries + ' (' + ((this.counterEntries / (this.totalEntries)) * 100).toFixed(2) + '%)'
          // 'percentagem': ((this.ht / (this.cancelledHtLoss + this.cancelledHtNoLoss + this.ht)) * 100).toFixed(2)
        }, {
          'estado': this.$t('pyramid.submitedTests'),
          'valor': this.ht > 0 ? this.ht : 0.0001,
          'leads': this.ht,
          'percentagem': ((this.ht / (this.totalEntries)) * 100).toFixed(2)
        }, {
          'estado': this.$t('pyramid.qualifiedLeads'),
          'valor': this.submittedHLCount > 0 ? this.submittedHLCount : 0.0001,
          'leads': this.submittedHLCount,
          'percentagem': ((this.submittedHLCount / this.totalEntries) * 100).toFixed(2),
          // 'morelabel': '(' + this.$t('pyramid.submitedWithLoss') + ')'
        }, {
          'estado': this.$t('pyramid.validContacts'),
          'valor': (this.scheduledAppointments + this.contactsSuccessHLCount) > 0 ? (this.scheduledAppointments + this.contactsSuccessHLCount) : 0.0001,
          'leads': (this.scheduledAppointments + this.contactsSuccessHLCount),
          'percentagem': (((this.scheduledAppointments + this.contactsSuccessHLCount) / this.totalEntries) * 100).toFixed(2)
        }, {
          'estado': this.$t('pyramid.scheduledAppointment'),
          'valor': this.scheduledAppointments > 0 ? this.scheduledAppointments : 0.0001,
          'leads': this.scheduledAppointments,
          'percentagem': ((this.scheduledAppointments / this.totalEntries) * 100).toFixed(2)
        }, {
          'estado': this.$t('pyramid.totalAppointments'),
          'valor': this.totalAppointments > 0 ? this.totalAppointments : 0.0001,
          'leads': this.totalAppointments,
          'percentagem': ((this.totalAppointments / this.totalEntries) * 100).toFixed(2),
          'morelabel': this.$t('pyramid.missedAppointment') + ': ' + this.appointmentMissedCount + ' (' + ((this.appointmentMissedCount / (this.totalEntries)) * 100).toFixed(2) + '%)'
        }, {
          'estado': this.$t('pyramid.lqe'),
          'valor': this.qualifiedCount > 0 ? this.qualifiedCount : 0.0001,
          'leads': this.qualifiedCount,
          'percentagem': ((this.qualifiedCount / this.totalEntries) * 100).toFixed(2)
        }
      ]
    }
  },
  watch: {
    entityOptions: {
      handler: function (options) {
        if (options.length === 1) {
          this.filters.entity = options[0]
        } else if (this.filters.entity && !options.some(option => option.id === this.filters.entity.id)) {
          this.filters.entity = null
        }
      },
      immediate: true
    },
    clusterOptions: {
      handler: function (options) {
        // Multiselect did not update label unless v-model was reset to null and then set again
        const previousValue = this.filters.cluster
        this.filters.cluster = null
        let newSelectedValue = null

        if (options.length === 1) {
          newSelectedValue = options[0]
        } else if (previousValue) {
          newSelectedValue = options.find(option => option.id === previousValue.id) || null
        }

        this.$nextTick(() => {
          this.filters.cluster = newSelectedValue
        })
      },
      immediate: true
    },
    filters: {
      handler: function () {
        LocalStorageHelper.set(PYRAMID_STATS, this.filters)
      },
      deep: true
    },
    'filters.showEntityStats': function (value) {
      if (!value && !this.clusters.length) {
        this.filters.showEntityStats = true
      }
    },
    requestParams: function (value) {
      this.loadBaseData()
    },
    chartData: function (value) {
      if (this.chart) {
        this.chart.data = value
      }
    }
  },
  mounted () {
    this.createChart()
    this.loadLocalStorage()
      .then(() => {
        this.loadBaseData()
      })
  },
  beforeDestroy () {
    if (this.chart) {
      this.chart.dispose()
    }
  }
}
</script>

<style lang="scss">
.pyramid-diagram {

  #chartdiv {
    width: 100%;
    height: 65vh;
  }

  .chart-label {
    border: 1px solid #a6a6a6;
    border-radius: .4rem;
    padding: 0.5rem;
  }

  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
    top: 40%;
    left: 50%;
    color: #2b6154
  }
}
</style>
